const currentLang = document.documentElement.lang.toUpperCase();
const STOREFRONT_API_URL = `https://${Shopify.shop}/api/2023-10/graphql.json`;
const STOREFRONT_API_ACCESS_TOKEN = '';

// Helper function to chunk the IDs
const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

// Gather all the Ids of the metaobjects in the facets
export const extractMetaobjectIds = (facets) => {
  if (!Array.isArray(facets)) {
    console.warn("Facets is not an array");
    return [];
  }

  const metaobjectIds = facets
    .flatMap(([, options]) => options || []) // Get all options
    .flatMap(option => (Array.isArray(option) ? option[0] : option)) // Get the first value
    .filter(id => typeof id === "string" && id.startsWith("gid://shopify/Metaobject/")); // Keep only valid IDs

  return metaobjectIds;
};

// Fetch the metaobjects from the GraphQL API
export const fetchMetaObjectsByIds = async (metaobjectIds) => {
  if (!metaobjectIds || metaobjectIds.length === 0) return [];

  // Break IDs into chunks of 250
  const chunkedIds = chunkArray(metaobjectIds, 250);

  try {
    const allResults = [];

    for (const chunk of chunkedIds) {
      const query = `
        query @inContext(language: ${currentLang}) {
          nodes(ids: ${JSON.stringify(chunk)}) {
            ...on Metaobject {
              id
              handle
              fields {
                key
                value
              }
            }
          }
        }
      `;

      // Fetch data for current chunk
      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
        },
        body: JSON.stringify({ query, variables: { ids: chunk} }),
      });

      if (!response.ok) {
        console.error(`Failed to fetch metaobjects: Unauthorized`);
        return [];
      }

      const data = await response.json();
      const nodes = data?.data?.nodes;

      if (Array.isArray(nodes)) {
        allResults.push(...nodes);
      } else {
        console.error("Nodes is not an array:", nodes);
        return [];
      }
    }

    return allResults;
  } catch (error) {
    console.error(`Failed to fetch metaobjects: ${error}`);
    return [];
  }
};

// Replace the metaobject IDs in the facets with the metaobject names
export const replaceMetaObjectIdswithValues = (filteredFacets, metaobjects) => {
  if (!Array.isArray(metaobjects)) {
    console.error(`Metaobjects is not iterable:`, metaobjects);
    return filteredFacets;
  }

  const metaobjectMap = new Map();
  metaobjects.forEach(metaobject => {
    if (metaobject && metaobject.id && metaobject.fields) {
      const valueField = metaobject.fields.find(field => field.key === 'value');
      if (valueField) {
        metaobjectMap.set(metaobject.id, valueField.value);
      }
    }
  });

  const updatedFacets = filteredFacets.map(facet => {
    if (!Array.isArray(facet) || facet.length < 5) {
      return facet;
    }

    const [facetName, options, label, isRange, sortOrder] = facet;

    if (Array.isArray(options)) {
      const updatedOptions = options.map((option) => {
        if (Array.isArray(option) && option.length >= 2) {
          const [optionValue, count] = option;

          if (typeof optionValue === "string" && optionValue.startsWith("gid://shopify/Metaobject/")) {
            const trimmedLabel = metaobjectMap.get(optionValue) || optionValue;
            return [optionValue, count, trimmedLabel];
          }
        }

       return option;
      });

      return [facetName, updatedOptions, label, isRange, sortOrder];
    }

    return facet;
  });

  return updatedFacets;
};

export const processFilteredFacets = async (filteredFacets) => {
  if (!Array.isArray(filteredFacets)) {
    console.error(`Expected filteredFacets to be an array, but got: ${filteredFacets}`);
    return [];
  }

  const metaobjectIds = extractMetaobjectIds(filteredFacets);

  if (metaobjectIds.length === 0) return filteredFacets; // No metaobjects to fetch

  try {
    const metaobjects = await fetchMetaObjectsByIds(metaobjectIds);

    if (!Array.isArray(metaobjects)) {
      console.error(`Metaobjects is not an array: ${metaobjects}`);
      return filteredFacets;
    }

    const updatedFacets = replaceMetaObjectIdswithValues(filteredFacets, metaobjects);
    return updatedFacets;
  } catch (error) {
    console.error(`Error fetching metaobjects: ${error}`);
    return filteredFacets;
  }
}
